import { apiInstance, gql } from "@/services/httpAdapter";

// Fragments
import { leadDispositionFragment, salesLeadCustomerFragment, salesLeadFragment } from "./fragments";

// Helpers
import { errorLogger } from "@/Common/helpers/logger";

// Types
import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { IPageParam, IPaginatedResponse } from "@/Common/api/types/pagination";
import { ISalesLeads, ISalesLeadsCustomer } from "@/DataPages/types/rci/salesLeads/model";
import { ILeadDisposition } from "@/DataPages/types/rci/leadDisposition/model";
interface IGetSalesLeadsArgs {
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	pagination: {
		first: number;
		after?: string;
	};
}

export async function getSalesLeads(args: IGetSalesLeadsArgs) {
	const { locationId, commissionCycleId, payPeriodId, pagination } = args;

	const query = gql`
		query getSalesLeads($locationId: String!, $commissionCycleId: String!, $payPeriodId: String!, $pagination: PaginationInput) {
			getSalesLeads(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				pagination: $pagination
			) {
				edges {
					cursor
					node {
						...SalesLead
					}
				}
				pageInfo {
					endCursor
					hasNextPage
				}
			}
		}
		${salesLeadFragment}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		pagination,
	};

	try {
		const response: GraphqlQueryResponse<"getSalesLeads", IPaginatedResponse<ISalesLeads>> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getSalesLeads;
	} catch (err) {
		if (err instanceof Error || typeof err === "string") {
			errorLogger(err);
		}
		return null;
	}
}

export async function getDispositions(locationId: string, commissionCycleId: string, payPeriodId: string, pagination: IPageParam<string>) {
	const query = gql`
		query getDispositions($locationId: String!, $commissionCycleId: String!, $payPeriodId: String!, $pagination: PaginationInput) {
			getDispositions(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				pagination: $pagination
			) {
				edges {
					cursor
					node {
						...LeadDisposition
					}
				}
				pageInfo {
					endCursor
					hasNextPage
				}
			}
		}
		${leadDispositionFragment}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		pagination,
	};

	try {
		const response: GraphqlQueryResponse<"getDispositions", IPaginatedResponse<ILeadDisposition>> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getDispositions;
	} catch (err) {
		if (err instanceof Error || typeof err === "string") {
			errorLogger(err);
		}
		return null;
	}
}

export async function getSalesLeadsCustomers(locationId: string, range: string, pagination: { first: number; after?: string }) {
	const query = gql`
		query getSalesLeadsCustomers($locationId: String!, $range: String!, $pagination: PaginationInput) {
			getSalesLeadsCustomers(locationId: $locationId, range: $range, pagination: $pagination) {
				edges {
					cursor
					node {
						...SalesLeadCustomer
					}
				}
				pageInfo {
					endCursor
					hasNextPage
				}
			}
		}
		${salesLeadCustomerFragment}
	`;

	const variables = {
		locationId,
		range,
		pagination,
	};

	try {
		const response: GraphqlQueryResponse<"getSalesLeadsCustomers", IPaginatedResponse<ISalesLeadsCustomer>> = await apiInstance.post(
			"/graphql",
			{
				query,
				variables,
			}
		);
		return response.data.data?.getSalesLeadsCustomers;
	} catch (err) {
		if (err instanceof Error || typeof err === "string") {
			errorLogger(err);
		}
		return null;
	}
}

export type TSearchVehicleInventory = {
	type: string;
	stockNumber: string;
	vin: string;
	make: string;
	model: string;
	style: string;
	year: string;
};

export interface IInventoryDesiredVehicles {
	_id: string;
	type: string;
	stockNumber: string;
	vin: string;
	year: string;
	make: string;
	model: string;
	style: string;
}

export async function getInventoryDesiredVehicles(
	locationId: string,
	filterInput: Partial<TSearchVehicleInventory>,
	pagination: { first: number; after: string }
) {
	const query = gql`
		query getInventoryDesiredVehicles(
			$locationId: String!
			$filterInput: InventoryDesiredVehiclesFilterInput
			$pagination: PaginationInput
		) {
			getInventoryDesiredVehicles(locationId: $locationId, filterInput: $filterInput, pagination: $pagination) {
				edges {
					node {
						_id
						type
						stockNumber
						vin
						year
						make
						model
						style
					}
					cursor
				}
				pageInfo {
					endCursor
					hasNextPage
				}
			}
		}
	`;

	const variables = {
		locationId,
		filterInput,
		pagination,
	};

	try {
		const response: GraphqlQueryResponse<
			"getInventoryDesiredVehicles",
			IPaginatedResponse<IInventoryDesiredVehicles>
		> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getInventoryDesiredVehicles as IPaginatedResponse<IInventoryDesiredVehicles, IPageParam<string>>;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}
